'use client';

// Reads the change queue's entries and lot states for the provider (BL-2438): once on open, again after
// every change the queue announces. Split from OfflineSyncProvider to keep that file under 500 lines.

import { useCallback, useEffect, useRef, useState } from 'react';
import type { LotDesiredState } from '@auctogon/admin-isomorphic/isomorphic/offline/lot-desired-state';
import type { OutboxEntry } from '../../gateways/native-device';
import type { ChangeQueueReplay } from '../../stores/change-queue/change-queue-replay';

const logClientError = (message: string, err: Error) => {
  // eslint-disable-next-line no-console -- client module; createLogger is server-only (ALS).
  console.error(message, err);
};

/**
 * Reads the queue on open and again after every change the queue announces. One read at a time:
 * a change announced mid-read reads once more when it finishes, so the last state always lands.
 */
export function useQueueEntries(queue: ChangeQueueReplay | null): {
  entries: OutboxEntry[] | null;
  lotStates: LotDesiredState[] | null;
  error: Error | null;
  refresh: () => void;
} {
  // Keyed on the queue it was read from, so a new scope's queue never shows the last one's rows.
  const [state, setState] = useState<{
    queue: ChangeQueueReplay | null;
    entries: OutboxEntry[] | null;
    lotStates: LotDesiredState[] | null;
    error: Error | null;
  }>({
    queue: null,
    entries: null,
    lotStates: null,
    error: null,
  });
  const readRef = useRef<(() => void) | null>(null);
  const refresh = useCallback(() => readRef.current?.(), []);
  useEffect(() => {
    if (queue == null) return;
    let live = true;
    let reading = false;
    let again = false;
    const read = async () => {
      if (reading) {
        again = true;
        return;
      }
      reading = true;
      try {
        do {
          again = false;
          const [entries, lotStates] = await Promise.all([queue.list(), queue.listLotStates()]);
          if (live) setState({ queue, entries, lotStates, error: null });
        } while (again && live);
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err));
        logClientError('change queue: could not read the queue', error);
        if (live) {
          setState((prev) => ({
            queue,
            entries: prev.queue === queue ? prev.entries : null,
            lotStates: prev.queue === queue ? prev.lotStates : null,
            error,
          }));
        }
      } finally {
        reading = false;
      }
    };
    readRef.current = () => void read();
    void read();
    const unsubscribe = queue.subscribe(() => void read());
    return () => {
      live = false;
      readRef.current = null;
      unsubscribe();
    };
  }, [queue]);
  return state.queue === queue && queue != null
    ? { entries: state.entries, lotStates: state.lotStates, error: state.error, refresh }
    : { entries: null, lotStates: null, error: null, refresh };
}
